import { yupResolver } from '@hookform/resolvers/yup';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Checkbox,
  CircularProgress,
  FormHelperText,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { compact, forEach, get, isEmpty, isEqual, map } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { runMultiplexCellTyping } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import {
  getModelsTypeByModelInferences,
  getResultNameTypesByOrchestrations,
  InferenceModelData,
  OrchestrationInference,
  ResultNameOrchestrations,
} from 'interfaces/calculateFeatures';
import { BinaryClassifierConfig, PipsResultsConfig } from 'interfaces/inferenceArtifacts';
import { ThresholdJob } from 'interfaces/job';
import { CellRules, MultiplexCellTypingConfig } from 'interfaces/jobs/multiplex/cellTypingParams';
import { Permission } from 'interfaces/permissionOption';
import { humanize } from 'utils/helpers';
import { useOrchestrationSelections } from 'utils/orchestrationSelections';
import { useCasesParams } from 'utils/useCasesParams';
import { usePermissions } from 'utils/usePermissions';
import { ActionStepper } from '../ActionStepper';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import ThresholdMergePreview from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides/ThresholdMergePreview';
import {
  buildThresholdMergeState,
  ThresholdConflictResolutions,
} from '../PostProcessingAndCalculateFeatures/thresholdMerge';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import { JobNameAndDescriptionStep } from '../utils';
import CellRulesFromPanelSelect from './CellRulesFromPanelSelect';
import { MultiplexSelectionAlert } from './MultiplexSelectionAlert';
import { ThresholdJobMultiSelect } from './ThresholdJobMultiSelect';
import { ThresholdJobSelect } from './ThresholdJobSelect';
import { useMultiplexActionSelection } from './useMultiplexActionSelection';

const SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_TYPING = 'RUN_MULTIPLEX_CELL_TYPING';

type CellTypingFormConfig = Omit<
  MultiplexCellTypingConfig,
  'thresholds' | 'perMarkerThresholdsOrchestrationId' | 'cellRules'
> & {
  thresholds: MultiplexCellTypingConfig['thresholds'] | null;
  perMarkerThresholdsOrchestrationId: string | null;
  cellRules: MultiplexCellTypingConfig['cellRules'] | null;
};

export interface CellTypingFormValues {
  jobName?: string;
  jobDescription?: string;
  configParams: CellTypingFormConfig;
}

const validationSchema = [
  yup.object({
    configParams: yup.object({
      cellRules: yup.object().nullable().required('Cell Rules are required'),
      binaryClassifierInferenceArtifactUrls: yup
        .array()
        .required()
        .min(1, 'Binary Classifier Inference Artifact is required'),
      thresholds: yup.object().nullable().required('Threshold results are required'),
      perMarkerThresholdsOrchestrationId: yup.string().nullable(),
    }),
  }),
  yup.object({}),
];

const defaultValues: CellTypingFormValues = {
  configParams: {
    binaryClassifierInferenceArtifactUrls: [],
    thresholds: null,
    perMarkerThresholdsOrchestrationId: null,
    cellRules: null,
  },
};

const defaultPerSlideSample = 5000;
type ThresholdJobSelectionMode = 'single' | 'multiple';

const knnInputHelperText =
  'Select one input per slide from Binary Classifier or Post Processing - Cell Typing. The selected cell table values are used to fit KNN.';

const buildMultiplexCellTypingConfig = (configParams: CellTypingFormConfig): MultiplexCellTypingConfig | null => {
  const { thresholds, perMarkerThresholdsOrchestrationId, cellRules, ...rest } = configParams;

  if (!thresholds || !cellRules) {
    return null;
  }

  return {
    ...rest,
    thresholds,
    cellRules,
    ...(perMarkerThresholdsOrchestrationId ? { perMarkerThresholdsOrchestrationId } : {}),
  };
};

export interface RunMultiplexCellTypingProps {
  onClose: (event?: any, force?: boolean) => void;
}

export const RunMultiplexCellTyping = (props: RunMultiplexCellTypingProps): ReactElement => {
  const { onClose } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [activeStep, setActiveStepState] = useState(0);
  const setActiveStep = (stepIndex: number) => {
    if (activeStep === 0) {
      // Trigger validation for step 0 before moving away, so we can show errors if any
      trigger('configParams');
    }
    setActiveStepState(stepIndex);
  };
  const currentValidationSchema = validationSchema[activeStep];

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const { casesParams } = useCasesParams();
  const {
    effectiveCasesParams,
    infoMessage: multiplexSelectionInfoMessage,
    blockingMessage: multiplexSelectionBlockingMessage,
    isLoadingSelection,
  } = useMultiplexActionSelection(casesParams);
  const multiplexCasesParams = effectiveCasesParams || casesParams;

  const studyId = multiplexCasesParams.filters?.studyId;

  const [selectedCellRulesPanelId, setSelectedCellRulesPanelId] = useState<string | null | undefined>(undefined);

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    trigger,
    setValue,
    formState: { errors, isDirty },
  } = useForm<CellTypingFormValues>({
    mode: 'onChange',
    defaultValues: defaultValues,
    resolver: yupResolver(currentValidationSchema),
  });
  useTrackActionFormDirty(isDirty);

  const enableInferenceArtifactsQuery = Boolean(studyId);

  const { data: inferenceArtifacts, isWaitingForResults: isWaitingForInferenceArtifacts } = useArtifactResultsQuery({
    caseParams: multiplexCasesParams,
    config: BinaryClassifierConfig,
    enabled: enableInferenceArtifactsQuery && Boolean(effectiveCasesParams),
  });

  const {
    data: pipsResultsArtifacts,
    isWaitingForResults: isWaitingForPipsResults,
    isError: isPipsResultsArtifactsError,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: multiplexCasesParams,
    config: PipsResultsConfig,
    enabled: enableInferenceArtifactsQuery && Boolean(effectiveCasesParams),
  });

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', multiplexCasesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(multiplexCasesParams, signal),
    enabled: Boolean(studyId) && Boolean(effectiveCasesParams),
  });

  const orchestrationSelectionsState = useOrchestrationSelections();
  const {
    selectedOrchestrations,
    selectedPipsOrchestrations,
    setSelectedOrchestrations,
    setSelectedPipsOrchestrations,
    setSelectedOrchestrationsByTypeByStain,
    updateSelectedPipsOrchestrations,
  } = orchestrationSelectionsState;
  const [selectedThresholdJobId, setSelectedThresholdJobId] = useState<string | null>(null);
  const [selectedThresholdJobIds, setSelectedThresholdJobIds] = useState<string[]>([]);
  const [selectedThresholdJobs, setSelectedThresholdJobs] = useState<ThresholdJob[]>([]);
  const [thresholdJobSelectionMode, setThresholdJobSelectionModeState] = useState<ThresholdJobSelectionMode>('single');
  const [thresholdConflictResolutions, setThresholdConflictResolutions] = useState<ThresholdConflictResolutions>({});
  const [perSlideSample, setPerSlideSample] = useState<boolean>(false);

  const thresholdMergeState = useMemo(
    () => buildThresholdMergeState(selectedThresholdJobs, thresholdConflictResolutions),
    [selectedThresholdJobs, thresholdConflictResolutions]
  );

  const setThresholdJobSelectionMode = useCallback(
    (nextMode: ThresholdJobSelectionMode) => {
      setThresholdJobSelectionModeState(nextMode);

      if (nextMode === 'multiple') {
        setSelectedThresholdJobIds((currentJobIds) =>
          !isEmpty(currentJobIds) || !selectedThresholdJobId ? currentJobIds : [selectedThresholdJobId]
        );
        return;
      }

      setSelectedThresholdJobIds([]);
      setSelectedThresholdJobs([]);
      setThresholdConflictResolutions({});

      if (!selectedThresholdJobId) {
        setValue('configParams.thresholds', null);
        setValue('configParams.perMarkerThresholdsOrchestrationId', null);
      }
    },
    [selectedThresholdJobId, setValue]
  );

  const setSelectedKnnBinaryClassifierOrchestrations = useCallback(
    (slideIds: string[], model?: InferenceModelData, modelType?: string, orchestration?: OrchestrationInference) => {
      if (isEmpty(model) || isEmpty(modelType) || isEmpty(orchestration)) {
        setSelectedOrchestrationsByTypeByStain(slideIds, model, modelType, orchestration);
        return;
      }

      setSelectedPipsOrchestrations((currentSelectedPipsOrchestrations) => {
        const nextSelectedPipsOrchestrations = { ...currentSelectedPipsOrchestrations };

        forEach(slideIds, (slideId) => {
          nextSelectedPipsOrchestrations[slideId] = {};
        });

        return nextSelectedPipsOrchestrations;
      });

      setSelectedOrchestrations((currentSelectedOrchestrations) => {
        const nextSelectedOrchestrations = { ...currentSelectedOrchestrations };

        forEach(slideIds, (slideId) => {
          nextSelectedOrchestrations[slideId] = {
            [modelType]: {
              model,
              orchestration,
            },
          };
        });

        return nextSelectedOrchestrations;
      });
    },
    [setSelectedOrchestrations, setSelectedOrchestrationsByTypeByStain, setSelectedPipsOrchestrations]
  );

  const updateSelectedKnnPipsOrchestrations = useCallback(
    (slideIds: string[], resultKey?: string, orchestration?: OrchestrationInference) => {
      if (isEmpty(resultKey) || isEmpty(orchestration)) {
        updateSelectedPipsOrchestrations(slideIds, resultKey, orchestration);
        return;
      }

      setSelectedOrchestrations((currentSelectedOrchestrations) => {
        const nextSelectedOrchestrations = { ...currentSelectedOrchestrations };

        forEach(slideIds, (slideId) => {
          nextSelectedOrchestrations[slideId] = {};
        });

        return nextSelectedOrchestrations;
      });

      setSelectedPipsOrchestrations((currentSelectedPipsOrchestrations) => {
        const nextSelectedPipsOrchestrations = { ...currentSelectedPipsOrchestrations };

        forEach(slideIds, (slideId) => {
          nextSelectedPipsOrchestrations[slideId] = {
            [resultKey]: {
              orchestration,
            },
          };
        });

        return nextSelectedPipsOrchestrations;
      });
    },
    [setSelectedOrchestrations, setSelectedPipsOrchestrations, updateSelectedPipsOrchestrations]
  );

  const knnOrchestrationSelectionsState = useMemo(
    () => ({
      ...orchestrationSelectionsState,
      setSelectedOrchestrationsByTypeByStain: setSelectedKnnBinaryClassifierOrchestrations,
      updateSelectedPipsOrchestrations: updateSelectedKnnPipsOrchestrations,
    }),
    [orchestrationSelectionsState, setSelectedKnnBinaryClassifierOrchestrations, updateSelectedKnnPipsOrchestrations]
  );

  const selectedKnnInputArtifactUrls = useMemo(() => {
    const artifactUrls: string[] = [];

    forEach(slides, (slide) => {
      const slideId = slide.slideId;
      const slideArtifactUrls = compact([
        ...map(selectedOrchestrations[slideId], (orchestrationData) =>
          orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern.replace('{slide_id}', slideId)
        ),
        ...map(selectedPipsOrchestrations[slideId], (orchestrationData) =>
          orchestrationData?.orchestration.orchestrationResultArtifactUrlPattern.replace('{slide_id}', slideId)
        ),
      ]);

      artifactUrls.push(...slideArtifactUrls);
    });

    return artifactUrls;
  }, [selectedOrchestrations, selectedPipsOrchestrations, slides]);

  useEffect(() => {
    const currentBinaryClassifierUrls = getValues('configParams.binaryClassifierInferenceArtifactUrls');
    // Preserve the backend field name while allowing either Binary Classifier or Post Processing cell-table inputs.
    if (!isEmpty(currentBinaryClassifierUrls) || !isEmpty(selectedKnnInputArtifactUrls)) {
      setValue('configParams.binaryClassifierInferenceArtifactUrls', selectedKnnInputArtifactUrls);
      trigger('configParams.binaryClassifierInferenceArtifactUrls');
    }
  }, [getValues, selectedKnnInputArtifactUrls, setValue, trigger]);

  useEffect(() => {
    if (thresholdJobSelectionMode !== 'multiple') {
      return;
    }

    setValue(
      'configParams.thresholds',
      isEmpty(thresholdMergeState.thresholds) ? null : thresholdMergeState.thresholds
    );
    setValue('configParams.perMarkerThresholdsOrchestrationId', thresholdMergeState.perMarkerThresholdsOrchestrationId);
    trigger('configParams.thresholds');
    trigger('configParams.perMarkerThresholdsOrchestrationId');
  }, [setValue, thresholdJobSelectionMode, thresholdMergeState, trigger]);

  const handleCellRulesChange: (newCellRules: CellRules | null) => void = useCallback(
    (newCellRules: CellRules | null) => {
      const currentCellRules = getValues('configParams.cellRules');

      if (isEqual(currentCellRules, newCellRules)) {
        return;
      }

      setValue('configParams.cellRules', newCellRules);
      trigger('configParams.cellRules');
    },
    [getValues, setValue, trigger]
  );

  const checkValidationAndSetIsStepFailed = (stepIndex: number, objectToValidate: Record<string, any>) => {
    validationSchema[stepIndex]
      .validate(objectToValidate)
      .then(() => {
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: false,
        }));
      })
      .catch((validationError) => {
        console.error('Validation error in step', stepIndex, validationError);
        setIsStepFailed((prev) => ({
          ...prev,
          [stepIndex]: true,
        }));
      });
  };

  const runMultiplexCellTypingMutation = useMutation(runMultiplexCellTyping, {
    onError: () => {
      enqueueSnackbar('Error occurred, Fit KNN Model For Multiplex Cell Typing failed', {
        variant: 'error',
      });
    },
    onSuccess: () => {
      enqueueSnackbar('Fit KNN Model For Multiplex Cell Typing Started', { variant: 'success' });
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_TYPING);
    },
  });

  const onSubmit: SubmitHandler<CellTypingFormValues> = async (data) => {
    if (thresholdJobSelectionMode === 'multiple' && thresholdMergeState.hasBlockingIssues) {
      setIsStepFailed((prev) => ({
        ...prev,
        [0]: true,
      }));
      enqueueSnackbar(
        !isEmpty(thresholdMergeState.invalidJobs)
          ? 'Selected threshold jobs must include per-marker thresholds before submitting.'
          : 'Resolve threshold job marker conflicts before submitting.',
        { variant: 'error' }
      );
      return;
    }

    const configParams = buildMultiplexCellTypingConfig(data.configParams);

    if (!configParams) {
      await trigger('configParams');
      setIsStepFailed((prev) => ({
        ...prev,
        [0]: true,
      }));
      enqueueSnackbar('Cell typing configuration is incomplete', { variant: 'error' });
      return;
    }

    runMultiplexCellTypingMutation.mutate({
      ...multiplexCasesParams,
      jobName: data.jobName,
      jobDescription: data.jobDescription,
      configParams,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Fit KNN Model For Multiplex Cell Typing to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_MULTIPLEX_CELL_TYPING,
      autoHideDuration: null,
    });

    onClose(undefined, true);
  };

  const validateCellTypingParams = async () => {
    checkValidationAndSetIsStepFailed(0, {
      configParams: watch('configParams'),
    });

    if (thresholdJobSelectionMode === 'multiple' && thresholdMergeState.hasBlockingIssues) {
      setIsStepFailed((prev) => ({
        ...prev,
        [0]: true,
      }));
    }
  };

  const onLoadFullJob = useCallback(
    (fullThresholdJob: ThresholdJob) => {
      const thresholds = fullThresholdJob.results?.perMarkerThresholds;
      if (thresholds) {
        setValue('configParams.thresholds', thresholds);
        trigger('configParams.thresholds');
        const orchestrationId = fullThresholdJob.orchestrationId;
        if (orchestrationId) {
          setValue('configParams.perMarkerThresholdsOrchestrationId', orchestrationId);
          trigger('configParams.perMarkerThresholdsOrchestrationId');
        }
      } else {
        console.error('Selected threshold job has no thresholds results', { fullThresholdJob });
        enqueueSnackbar('Selected threshold job has no thresholds results', { variant: 'error' });
      }
    },
    [setValue, trigger]
  );

  const onLoadThresholdJobs = useCallback((fullThresholdJobs: ThresholdJob[]) => {
    setSelectedThresholdJobs(fullThresholdJobs);
  }, []);

  const thresholdsErrorMessage = get(errors, 'configParams.thresholds')?.message;
  const cellRulesErrorMessage = get(errors, 'configParams.cellRules')?.message;
  const inputModelsType = useMemo(
    () => [
      ...getModelsTypeByModelInferences(inferenceArtifacts),
      ...getResultNameTypesByOrchestrations(pipsResultsArtifacts),
    ],
    [inferenceArtifacts, pipsResultsArtifacts]
  );

  const steps = [
    {
      label: 'Cell Typing Configuration',
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            {get(errors, 'configParams.binaryClassifierInferenceArtifactUrls') && (
              <Typography color="error">
                {humanize(get(errors, 'configParams.binaryClassifierInferenceArtifactUrls')?.message)}
              </Typography>
            )}
            {isPipsResultsArtifactsError ? (
              <Typography color="error">Error fetching Post Processing - Cell Typing results</Typography>
            ) : null}
            <Alert severity="info" sx={{ mb: 1 }}>
              {knnInputHelperText}
            </Alert>
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slides}
              modelsType={inputModelsType}
              inferenceModels={inferenceArtifacts}
              pipsResultsArtifacts={pipsResultsArtifacts}
              displayPipsResults
              isLoading={isSlidesFetching}
              isLoadingInferenceResults={isWaitingForInferenceArtifacts}
              isLoadingPipsResults={isWaitingForPipsResults}
              {...knnOrchestrationSelectionsState}
            />
          </Grid>
          <Grid item>
            <Grid container spacing={1.25} direction="column">
              <Grid item>
                <Typography variant="subtitle2">Thresholds</Typography>
                <FormHelperText>
                  Use one threshold job or merge marker-exclusive threshold jobs before fitting KNN.
                </FormHelperText>
              </Grid>
              <Grid item>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={thresholdJobSelectionMode}
                  onChange={(_event, nextMode: ThresholdJobSelectionMode | null) => {
                    if (nextMode) {
                      setThresholdJobSelectionMode(nextMode);
                    }
                  }}
                  aria-label="Threshold job selection mode"
                >
                  <ToggleButton value="single" aria-label="Single threshold job">
                    Single job
                  </ToggleButton>
                  <ToggleButton value="multiple" aria-label="Multiple threshold jobs">
                    Multiple jobs
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item>
                {thresholdJobSelectionMode === 'multiple' ? (
                  <Grid container spacing={1.25} direction="column">
                    <Grid item>
                      <ThresholdJobMultiSelect
                        selectedThresholdJobIds={selectedThresholdJobIds}
                        setSelectedThresholdJobIds={setSelectedThresholdJobIds}
                        onLoadFullJobs={onLoadThresholdJobs}
                        error={Boolean(thresholdsErrorMessage)}
                        errors={errors}
                        field="configParams.thresholds"
                        helperText="Select the threshold jobs to merge by marker."
                        studyId={studyId}
                      />
                    </Grid>
                    <Grid item>
                      <ThresholdMergePreview
                        mergeState={thresholdMergeState}
                        setConflictResolutions={setThresholdConflictResolutions}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <ThresholdJobSelect
                    selectedThresholdJobId={selectedThresholdJobId}
                    setSelectedThresholdJobId={setSelectedThresholdJobId}
                    onLoadFullJob={onLoadFullJob}
                    error={Boolean(thresholdsErrorMessage)}
                    errors={errors}
                    field="configParams.thresholds"
                    autoAssignLastJob
                    helperText="Use one completed threshold job."
                    studyId={studyId}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <CellRulesFromPanelSelect
              studyId={studyId}
              selectedPanelId={selectedCellRulesPanelId}
              onSelectPanel={setSelectedCellRulesPanelId}
              onCellRulesChange={handleCellRulesChange}
              showRequiredError={Boolean(cellRulesErrorMessage)}
              autoAssignLastPanel
            />
            {Boolean(cellRulesErrorMessage) && <FormHelperText>{`${cellRulesErrorMessage}`}</FormHelperText>}
          </Grid>
          <Grid item container direction="row">
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <Typography variant="body2">Per Slide Sample: </Typography>
              </Grid>
              <Grid item>
                <Checkbox
                  checked={perSlideSample}
                  onChange={(e) => {
                    setPerSlideSample(e.target.checked);
                    if (e.target.checked && watch('configParams.perSlideSample') === undefined) {
                      setValue('configParams.perSlideSample', defaultPerSlideSample);
                    } else if (!e.target.checked && watch('configParams.perSlideSample') !== undefined) {
                      setValue('configParams.perSlideSample', undefined);
                    }
                  }}
                />
              </Grid>
            </Grid>
            {perSlideSample && (
              <Grid item>
                <Controller
                  control={control}
                  name={'configParams.perSlideSample'}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      label="Sample"
                      type="number"
                      fullWidth
                      value={value}
                      onChange={(event) => {
                        onChange(event.target.value);
                      }}
                      error={Boolean(get(errors, 'configParams.perSlideSample')?.message)}
                      helperText={humanize(get(errors, 'configParams.perSlideSample')?.message)}
                    />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: validateCellTypingParams,
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
  ];

  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  if (isLoadingSelection) {
    return (
      <MultiplexSelectionAlert
        severity="info"
        title="Checking slide selection"
        message="Resolving the multiplex slides for the current case selection."
      />
    );
  }

  if (multiplexSelectionBlockingMessage) {
    return (
      <MultiplexSelectionAlert
        severity="error"
        title="Select slides for this multiplex action"
        message={multiplexSelectionBlockingMessage}
      />
    );
  }

  return (
    <Grid
      container
      direction="column"
      spacing={2}
      sx={{
        // Allow flex/grid children to shrink so wide content (e.g. the dense
        // cell-rules DataGrid) scrolls inside its own container instead of
        // widening the whole dialog. Widths are left to MUI so column layouts
        // (e.g. the two-column inference/summary area) are preserved.
        '& .MuiGrid-root': { minWidth: 0 },
      }}
    >
      {multiplexSelectionInfoMessage ? (
        <Grid item>
          <MultiplexSelectionAlert
            severity="info"
            title="This action runs on slides"
            message={multiplexSelectionInfoMessage}
          />
        </Grid>
      ) : null}
      <Grid item>
        <ActionStepper
          handleSubmit={handleSubmit(onSubmit)}
          steps={steps}
          setActiveStepForValidation={setActiveStep}
          isStepFailed={isStepFailed}
        />
      </Grid>
      {canViewJsonJobParams && (
        <Grid item>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandIcon />}>Cell Typing Params Summary (JSON)</AccordionSummary>
            <AccordionDetails>
              <Typography component="pre">
                {JSON.stringify(
                  {
                    jobName: watch('jobName'),
                    jobDescription: watch('jobDescription'),
                    configParams: watch('configParams'),
                  },
                  null,
                  2
                )}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </Grid>
  );
};
